import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';

export type CreatedUserPasswordInfo = {
  email: string;
  fullName: string;
  temporaryPassword: string;
};

interface UserCreatedPasswordModalProps {
  open: boolean;
  info: CreatedUserPasswordInfo | null;
  onClose: () => void;
}

const UserCreatedPasswordModal: React.FC<UserCreatedPasswordModalProps> = ({
  open,
  info,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = info.temporaryPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  if (!info) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon sx={{ color: '#10b981' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Usuario creado
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Guarde esta contraseña temporal ahora. No se volverá a mostrar. El usuario deberá
            cambiarla en su primer ingreso.
          </Alert>

          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Usuario
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {info.email}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {info.fullName}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Contraseña temporal"
            value={info.temporaryPassword}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy} edge="end" aria-label="Copiar contraseña">
                    {copied ? <CheckIcon color="success" /> : <CopyIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={handleClose}>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreatedPasswordModal;
